import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { map } from 'lodash/fp';
import {
  FormControl, InputLabel, MenuItem, OutlinedInput, Select as MuiSelect,
} from '@material-ui/core';

const S = {
  InputLabel: styled(InputLabel)`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  Select: styled(MuiSelect)`
    .MuiSelect-icon {
      right: 0.5rem;
      color: ${({ disabled, theme }) => (disabled ? theme.palette.grey[300] : 'rgba(0, 0, 0, 0.54)')};
    }
    .MuiSelect-select {
      display: flex;
      align-items: center;
      color: ${({ theme }) => theme.palette.grey[900]};
      font-size: ${({ theme }) => theme.typography.pxToRem(15)};
      font-weight: 400;
      background-color: transparent;
    }
    .MuiSelect-select.Mui-disabled {
      color: ${({ theme }) => theme.palette.grey[300]};
    }
    &.Mui-focused .MuiSelect-icon {
      color: ${({ disabled, theme }) => (disabled ? theme.palette.grey[300] : theme.palette.primary.main)};
    }
    :hover {
      .MuiSelect-icon {
        color: ${({ disabled, theme }) => (disabled ? theme.palette.grey[300] : theme.palette.primary.main)};
      }
    }
    ${({ error, theme }) => error && css`
      .MuiSelect-icon {
        color: ${theme.palette.error.main};
      }
      :hover {
        .icon {
          color: ${theme.palette.error.main};
        }
      }
      &.Mui-focused .MuiSelect-icon {
        color: ${theme.palette.error.main};
      }
    `}
  `,
  OutlinedInput: styled(({ classes, ...otherProps }) => (
    <OutlinedInput
      classes={{
        ...classes,
        notchedOutline: 'notchedOutline',
      }}
      {...otherProps}
    />
  ))`
    .notchedOutline {
      border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    }
  `,
  MenuItem: styled(MenuItem)`
    min-height: 40px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
    &.Mui-selected:hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
  `,
};

/**
 * Select를 사용하여 원하는 아이템 목록을 선택할 수 있습니다.
 *
 * @version 1.0.0
 * @author [chayeoi](https://github.com/chayeoi)
 */

const Select = ({
  className, error, id, label, options, required, value, onChange, disabled, ...otherProps
}) => {
  const [labelWidth, setLabelWidth] = useState(0);

  const inputLabel = useRef(null);

  const measuredRef = useCallback((node) => {
    if (node) {
      inputLabel.current = node;
    }
  }, []);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  return (
    <FormControl
      className={className}
      error={error}
      required={required}
      variant="outlined"
      disabled={disabled}
    >
      {label && (
        <S.InputLabel ref={measuredRef} htmlFor={id} margin="dense">
          {label}
        </S.InputLabel>
      )}
      <S.Select
        value={value}
        error={error}
        onChange={onChange}
        input={(
          <S.OutlinedInput
            labelWidth={labelWidth}
            name="select"
            id="select"
            margin="dense"
          />
        )}
        disabled={disabled}
        {...otherProps}
      >
        {map(option => (
          <S.MenuItem key={option.id} value={option.value}>
            {option.label}
          </S.MenuItem>
        ), options)}
      </S.Select>
    </FormControl>
  );
};

Select.propTypes = {
  /** @ignore */
  className: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  /** 옵션 아이템 목록 */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
    }),
  ),
  /** 필수 항목 여부 */
  required: PropTypes.bool,
  /** 현재 활성화된 아이템 */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]).isRequired,
  /** 선택된 옵션이 변경될 때 호출될 콜백 함수 */
  onChange: PropTypes.func.isRequired,
};

Select.defaultProps = {
  className: undefined,
  disabled: false,
  error: false,
  id: undefined,
  label: undefined,
  options: [],
  required: false,
};

export default Select;
